import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, ArrowRight, ShoppingBag } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { getOrderStatus, type OrderStatus } from '../api/billing';

const POLL_INTERVAL_MS = 2500;
const MAX_POLL_ATTEMPTS = 12; // ~30s of polling before giving up and asking the user to check later

interface Props {
  /** Which ePay backLink this route corresponds to — used only as a hint before the first status check. */
  expected: 'success' | 'failure';
}

export default function PaymentResultPage({ expected }: Props) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const invoiceId = searchParams.get('invoice_id');

  const [status, setStatus] = useState<OrderStatus | 'checking' | 'unknown' | 'fulfilling'>('checking');
  const [attempts, setAttempts] = useState(0);

  const checkOnce = useCallback(async () => {
    if (!invoiceId) { setStatus('unknown'); return; }
    try {
      const result = await getOrderStatus(invoiceId);
      setStatus(result.status === 'paid' && result.fulfilled === false ? 'fulfilling' : result.status);
      if (result.status === 'paid' && result.fulfilled !== false) await refreshUser();
    } catch {
      setStatus((previous) => previous === 'fulfilling' ? previous : 'unknown');
    }
  }, [invoiceId, refreshUser]);

  useEffect(() => { checkOnce(); }, [checkOnce]);

  useEffect(() => {
    if (status !== 'pending' && status !== 'fulfilling') return;
    if (attempts >= MAX_POLL_ATTEMPTS) return;
    const timer = setTimeout(() => {
      void checkOnce().finally(() => setAttempts((a) => a + 1));
    }, POLL_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [status, attempts, checkOnce]);

  const stillPending = (status === 'pending' || status === 'fulfilling') && attempts >= MAX_POLL_ATTEMPTS;

  return (
    <div className="min-h-full bg-slate-50 font-sans flex items-center justify-center p-6">
      <div className="max-w-sm w-full bg-white rounded-3xl border border-gray-100 shadow-sm p-8 text-center">
        {(status === 'checking' || ((status === 'pending' || status === 'fulfilling') && !stillPending)) && (
          <>
            <div className="w-14 h-14 rounded-full bg-goszakup-50 flex items-center justify-center mx-auto mb-5">
              <Loader2 className="w-7 h-7 text-goszakup-600 animate-spin" />
            </div>
            <h1 className="font-extrabold text-gray-900 mb-2">{t(status === 'fulfilling' ? 'payment.fulfillingTitle' : 'payment.checkingTitle')}</h1>
            <p className="text-sm text-gray-500 leading-relaxed">{t(status === 'fulfilling' ? 'payment.fulfillingBody' : 'payment.checkingBody')}</p>
          </>
        )}

        {status === 'paid' && (
          <>
            <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            </div>
            <h1 className="font-extrabold text-gray-900 mb-2">{t('payment.successTitle')}</h1>
            <p className="text-sm text-gray-500 leading-relaxed mb-6">{t('payment.successBody')}</p>
            <div className="flex flex-col gap-2.5">
              <button
                onClick={() => navigate('/profile')}
                className="w-full py-3 rounded-2xl bg-goszakup-600 text-white text-sm font-extrabold hover:bg-goszakup-700 transition-colors flex items-center justify-center gap-2"
              >
                {t('payment.goProfile')} <ArrowRight className="w-4 h-4" />
              </button>
              <Link to="/plans" className="w-full py-3 rounded-2xl border border-gray-200 text-gray-600 text-sm font-bold hover:bg-gray-50 transition-colors">
                {t('payment.goPlans')}
              </Link>
            </div>
          </>
        )}

        {(status === 'failed' || (status === 'unknown' && expected === 'failure')) && (
          <>
            <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-5">
              <XCircle className="w-7 h-7 text-red-500" />
            </div>
            <h1 className="font-extrabold text-gray-900 mb-2">{t('payment.failedTitle')}</h1>
            <p className="text-sm text-gray-500 leading-relaxed mb-6">{t('payment.failedBody')}</p>
            <Link
              to="/plans"
              className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-2xl bg-goszakup-600 text-white text-sm font-extrabold hover:bg-goszakup-700 transition-colors"
            >
              <ShoppingBag className="w-4 h-4" /> {t('payment.goPlans')}
            </Link>
          </>
        )}

        {(stillPending || (status === 'unknown' && expected !== 'failure')) && (
          <>
            <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-5">
              <Loader2 className="w-7 h-7 text-amber-600" />
            </div>
            <h1 className="font-extrabold text-gray-900 mb-2">{t(status === 'fulfilling' ? 'payment.fulfillingTitle' : 'payment.pendingTitle')}</h1>
            <p className="text-sm text-gray-500 leading-relaxed mb-6">{t(status === 'fulfilling' ? 'payment.fulfillingLater' : 'payment.pendingBody')}</p>
            <Link to="/profile" className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-2xl bg-goszakup-600 text-white text-sm font-extrabold hover:bg-goszakup-700 transition-colors">
              {t('payment.goProfile')} <ArrowRight className="w-4 h-4" />
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
